/**
 * product-view-event.js
 *
 * POSTs a product-view event to
 * submissions.quicksignage.com/api/webhook/updateAirTableAtcEvents.
 *
 * Fires at most once per page load, and only when:
 *   1. the page has fully loaded (window `load`), and
 *   2. the user has interacted — scrolled ≥ 10px or moved the cursor ≥ 10px
 *      (trusted events only, so synthetic bot events are ignored), and
 *   3. a real location is available. Location is mandatory: if no valid
 *      city/region + country can be resolved within LOCATION_WAIT_MS the
 *      event is NOT sent.
 *
 * Location is read from the localStorage keys written by
 * layout/theme.liquid's fetchIPDetails() (`user_location_info`, `locationxh`).
 * The "Unknown City / 0.0.0.0" placeholder that fetchIPDetails() writes on
 * failure is treated as missing, and triggers one re-fetch.
 */
(function () {
  var API_URL = 'https://submissions.quicksignage.com/api/webhook/updateAirTableAtcEvents';
  var PAYMENT_LINK_HOST = 'quicksignage.com';
  var SCROLL_THRESHOLD_PX = 10;
  var MOUSE_THRESHOLD_PX = 10;
  var LOCATION_POLL_MS = 500;
  var LOCATION_WAIT_MS = 15000;

  var fired = false;

  function readLS(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function isRealValue(v) {
    if (v == null) return false;
    var s = String(v).trim();
    if (!s) return false;
    var low = s.toLowerCase();
    return low !== 'undefined' && low !== 'null' && low !== 'n/a' && low.indexOf('unknown') !== 0;
  }

  function isRealIp(v) {
    return isRealValue(v) && String(v).trim() !== '0.0.0.0';
  }

  /**
   * Returns { city, region, country, ip } with only real values filled in.
   */
  function readLocation() {
    var loc = { city: '', region: '', country: '', ip: '' };

    function fill(key, v) {
      if (!loc[key] && (key === 'ip' ? isRealIp(v) : isRealValue(v))) loc[key] = String(v).trim();
    }

    try {
      var info = JSON.parse(readLS('user_location_info') || 'null');
      if (info) {
        fill('city', info.city);
        fill('region', info.region || info.regionName || info.state);
        fill('country', info.country_name || info.countryName || info.country);
        fill('ip', info.ip || info.query);
      }
    } catch (e) { /* ignore parse errors */ }

    // locationxh format: "city, region, country, timezone, ip"
    var xh = readLS('locationxh');
    if (xh) {
      var parts = xh.split(', ');
      fill('city', parts[0]);
      fill('region', parts[1]);
      fill('country', parts[2]);
      fill('ip', parts[4]);
    }

    return loc;
  }

  function hasValidLocation(loc) {
    return !!loc.country && !!(loc.city || loc.region);
  }

  /**
   * Resolves with a valid location, or null if none shows up in time.
   * theme.liquid kicks off fetchIPDetails() on first visit, so we poll for
   * it; if a stale placeholder is cached, we ask for one fresh fetch.
   */
  function waitForLocation() {
    return new Promise(function (resolve) {
      var loc = readLocation();
      if (hasValidLocation(loc)) return resolve(loc);

      if (readLS('location') && typeof window.fetchIPDetails === 'function') {
        window.fetchIPDetails();
      }

      var waited = 0;
      var timer = setInterval(function () {
        waited += LOCATION_POLL_MS;
        var l = readLocation();
        if (hasValidLocation(l)) {
          clearInterval(timer);
          resolve(l);
        } else if (waited >= LOCATION_WAIT_MS) {
          clearInterval(timer);
          resolve(null);
        }
      }, LOCATION_POLL_MS);
    });
  }

  function readProductHandle() {
    try {
      var el = document.getElementById('json-data');
      var data = el ? JSON.parse(el.textContent) : null;
      if (data && data.handle) return data.handle;
    } catch (e) { /* fall through to URL */ }
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : '';
  }

  function sendEvent(loc) {
    var handle = readProductHandle();
    var pageUrl = window.location.href.replace(/^https?:\/\//, '');

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        payment_link: PAYMENT_LINK_HOST + '/products/' + handle,
        payment_link_original: pageUrl,
        // Same "city,region, country" format the inline snippets used to send.
        location: loc.city + ',' + loc.region + ', ' + loc.country,
        ipaddress: loc.ip,
        source: readLS('qs_source') || pageUrl,
        entry_point_url: readLS('entry_point_url')
      }),
      keepalive: true
    }).catch(function (error) {
      console.error('[product-view-event] request failed', error);
    });
  }

  function trigger() {
    if (fired) return;
    fired = true;
    unbind();
    waitForLocation().then(function (loc) {
      if (!loc) {
        console.warn('[product-view-event] no valid location, event not sent');
        return;
      }
      sendEvent(loc);
    });
  }

  var startScrollY = 0;
  var lastMouse = null;
  var mouseDistance = 0;

  function onScroll(e) {
    if (e && e.isTrusted === false) return;
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (Math.abs(y - startScrollY) >= SCROLL_THRESHOLD_PX) trigger();
  }

  function onMouseMove(e) {
    if (!e.isTrusted) return;
    if (lastMouse) {
      mouseDistance += Math.abs(e.clientX - lastMouse.x) + Math.abs(e.clientY - lastMouse.y);
      if (mouseDistance >= MOUSE_THRESHOLD_PX) trigger();
    }
    lastMouse = { x: e.clientX, y: e.clientY };
  }

  function unbind() {
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mousemove', onMouseMove);
  }

  function bind() {
    startScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('mousemove', onMouseMove, { passive: true });
  }

  if (document.readyState === 'complete') {
    bind();
  } else {
    window.addEventListener('load', bind);
  }
})();
